import { NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  input,
  signal,
  TemplateRef,
  viewChild,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';

// eslint-disable-next-line boundaries/external
import { DOMParser as PMDOMParser } from 'prosemirror-model';
import { take } from 'rxjs';
import { map } from 'rxjs/operators';

import { TranslatePipe } from '@core/i18n-legacy';
import { CreateWithAiButtonComponent } from '@core/offers-ui';
import { RenderDirective } from '@core/prosemirror-legacy/angular';
import {
  ProsemirrorRichConfig,
  ProsemirrorRichConfigDirective,
  WysiwygComponent,
} from '@core/prosemirror-legacy/webapp';
import { CompanyVerificationDataService } from '@core/tellent-company-verification-logic';
import { CompanyVerificationRequestStatus } from '@core/tellent-company-verification-types';
import { TellentAccessService } from '@core/tellent-logic';
import { TellentPositionsDataFacade } from '@core/tellent-positions-logic';
import { TellentPosition } from '@core/tellent-positions-types';
import {
  AiLogoComponent,
  ButtonComponent,
  ButtonLoadingDirective,
  EmptyStateComponent,
  IconComponent,
} from '@core/user-interface-legacy';

import {
  PositionDescriptionAiResponse,
  PositionDescriptionInsertAiMessageComponent,
} from './position-description-insert-ai-message/position-description-insert-ai-message.component';

export enum DescriptionMode {
  Edit = 'edit',
  View = 'view',
}

@Component({
  selector: 'tlc-position-details-description',
  templateUrl: './position-details-description.component.html',
  styleUrls: ['./position-details-description.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    AiLogoComponent,
    ButtonComponent,
    CreateWithAiButtonComponent,
    EmptyStateComponent,
    IconComponent,
    NgTemplateOutlet,
    PositionDescriptionInsertAiMessageComponent,
    ProsemirrorRichConfigDirective,
    ReactiveFormsModule,
    RenderDirective,
    TranslatePipe,
    WysiwygComponent,
    ButtonLoadingDirective,
  ],
})
export class PositionDetailsDescriptionComponent {
  private readonly companyVerificationService = inject(CompanyVerificationDataService);
  private readonly positionsDataFacade = inject(TellentPositionsDataFacade);

  private readonly aiFooterTemplate = viewChild.required<TemplateRef<any>>('aiFooterTemplate');

  protected readonly DescriptionMode = DescriptionMode;
  public readonly canManageUsers = inject(TellentAccessService).canManageTellentUsers;

  public readonly positionData = input.required<TellentPosition | null>();
  public readonly isAccountVerified = toSignal(
    this.companyVerificationService.status$.pipe(
      map(status => status === CompanyVerificationRequestStatus.Approved),
    ),
    { initialValue: false },
  );

  public get footerTemplate(): TemplateRef<any> {
    return this.aiFooterTemplate();
  }

  public get descriptionJsonValue(): object | null {
    return <object | null>this.descriptionJson.value;
  }

  public readonly ProsemirrorRichConfig = ProsemirrorRichConfig;
  public readonly wysiwygRef = viewChild(WysiwygComponent);
  public readonly descriptionJson = new FormControl<unknown>(null);
  public readonly mode = signal<DescriptionMode | null>(null);
  public readonly aiResponse = signal<PositionDescriptionAiResponse | null>(null);
  public readonly aiIsLoading = signal(false);
  public readonly showAiPanel = signal(false);
  public readonly loading = signal(false);

  public readonly showEditor = computed(() => this.mode() === DescriptionMode.Edit);

  public readonly showContent = computed(
    () =>
      this.mode() === DescriptionMode.View ||
      (!this.mode() && !!this.positionData()?.descriptionJson),
  );

  constructor() {
    effect(() => {
      const position = this.positionData();
      if (position?.descriptionJson) {
        this.descriptionJson.setValue(position.descriptionJson, { emitEvent: false });
        if (!this.mode()) {
          this.mode.set(DescriptionMode.View);
        }
      } else {
        this.descriptionJson.setValue(null, { emitEvent: false });
        const currentMode = this.mode();
        if (currentMode && currentMode !== DescriptionMode.Edit) {
          this.mode.set(null);
        }
      }
    });
  }

  public onAddDescription(): void {
    this.mode.set(DescriptionMode.Edit);
  }

  public onEdit(): void {
    this.mode.set(DescriptionMode.Edit);
  }

  public onGenerateWithAi(): void {
    this.mode.set(DescriptionMode.Edit);
    this.showAiPanel.set(true);
    this.aiResponse.set(null);
    this.aiIsLoading.set(true);
  }

  public onWriteWithAi(): void {
    this.mode.set(DescriptionMode.Edit);
    this.showAiPanel.set(true);
    this.aiResponse.set(null);
  }

  public onAiClose(): void {
    this.showAiPanel.set(false);
    this.aiResponse.set(null);
    this.aiIsLoading.set(false);
  }

  public onAiGenerate(_prompt: string): void {
    this.aiIsLoading.set(true);
  }

  public onAiInsert(content: string): void {
    const parsedContent = new DOMParser().parseFromString(content, 'text/html');
    this.wysiwygRef()?.insertHtml(parsedContent);
    this.onAiClose();
  }

  public onAiReplace(content: string): void {
    const editorRef = this.wysiwygRef();
    if (editorRef?.editorView) {
      const { state } = editorRef.editorView;
      const { schema, tr } = state;
      const parsedContent = new DOMParser().parseFromString(content, 'text/html');
      const doc = PMDOMParser.fromSchema(schema).parse(parsedContent);
      tr.replaceWith(0, state.doc.content.size, doc);
      editorRef.editorView.dispatch(tr);
    }
    this.onAiClose();
  }

  public onAiImprove(_prompt: string): void {
    this.aiIsLoading.set(true);
  }

  public onSave(): void {
    const position = this.positionData();
    if (!position) {
      return;
    }

    this.loading.set(true);
    const guid = position.guid;
    const descriptionJson = this.descriptionJson.value;

    this.positionsDataFacade
      .updateDescription(guid, descriptionJson)
      .pipe(take(1))
      .subscribe(result => {
        this.loading.set(false);
        if (result?.isSuccess) {
          this.mode.set(descriptionJson ? DescriptionMode.View : null);
        }
      });
  }

  public onCancel(): void {
    const originalValue = this.positionData()?.descriptionJson ?? null;
    this.descriptionJson.setValue(originalValue, { emitEvent: false });
    this.mode.set(originalValue ? DescriptionMode.View : null);
  }
}
